import dayjs from 'dayjs'

import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { ValueFormatterParams } from '@ag-grid-community/core'

dayjs.extend(utc)
dayjs.extend(timezone)

type Options = {
  includeTime?: boolean
}

// Will Format date to the given options
export const formatDate = (
  date: string | Date,
  { includeTime = false }: Options = {},
) => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    // will used in future if needed
    // timeZone: 'UTC', // default timezone: system
  }

  if (includeTime) {
    options.hour = 'numeric'
    options.minute = 'numeric'
    options.second = 'numeric'
  }

  return new Date(date).toLocaleDateString('en-GB', options)
}

const DUBAI_TZ = 'Asia/Dubai'

/**
 * Date only, pinned to Dubai local time (e.g. "20-Aug-2026").
 *
 * The SAP-sourced stock-out report captures its timestamps against the Dubai
 * clock and stores the time-of-day next to them as a pre-formatted string.
 * Rendering the date in the viewer's own timezone would let the two columns
 * disagree - and shift the date by a day for anyone outside the UAE - so it is
 * pinned here too.
 */
export const formatDubaiDate = (date?: Date | string | null) =>
  date ? dayjs(date).tz(DUBAI_TZ).format('DD-MMM-YYYY') : ''

/** Clock time only, pinned to Dubai local time (e.g. "4:00 PM"). */
export const formatDubaiTime = (date?: Date | string | null) =>
  date ? dayjs(date).tz(DUBAI_TZ).format('h:mm A') : ''

/**
 * Will Remove the time from the UTC date string and return date in yyyy-mm-dd
 */
export const getOnlyDate = (date?: Date | string) => {
  if (!date) {
    return ''
  }
  return dayjs(date).format('YYYY-MM-DD')
}

export const formatOrderDate = (date: Date | string) => {
  return dayjs.utc(date).format('DD/MM/YY hh:mm A')
}

export const convertTo12HourFormat = (date: Date | string) => {
  return dayjs(date).format('DD/MM/YY hh:mm A')
}

export const conditionalDateFormatter = (params: ValueFormatterParams) => {
  const value = params.value
  if (!value) return 'N/A'
  return convertTo12HourFormat(value)
}

/**
 * Converts a local calendar date (yyyy-mm-dd, as picked from a date input)
 * to the UTC instant of the start of that day in the user's local timezone.
 */
export const startOfDayUtc = (date: string) => {
  return dayjs(date).startOf('day').utc().toISOString()
}

/**
 * Converts a local calendar date (yyyy-mm-dd, as picked from a date input)
 * to the UTC instant of the end of that day in the user's local timezone.
 */
export const endOfDayUtc = (date: string) => {
  return dayjs(date).endOf('day').utc().toISOString()
}
